import type { ManeuverDto, MissionDto, MissionRecord } from '@trajectory/contracts';
import { loadRun } from '@/lib/playbackClock';
import { createDocumentStore } from './createDocumentStore';
import { useTelemetryStore } from './telemetryStore';

export const useMissionStore = createDocumentStore<MissionDto>();

/**
 * A `MissionRecord` (what every GET/list/create/restore response returns)
 * carries fields `MissionDto` doesn't — `projectId`, `ownerId`, `createdAt`,
 * `updatedAt`, `version`. Loading a record into the store *as-is* used to
 * leave those riding along in `document`, and since `useAutosave` PATCHes
 * with the whole current document, a mission with no project sent
 * `projectId: null` back to the server — which rejects it, because
 * `projectId` there is `IdSchema.optional()` (accepts a string or
 * `undefined`, not `null`). That surfaced as autosave silently failing
 * with a 400 on every newly-created, project-less mission — found by an
 * actual end-to-end test, not by inspection. Always load through this.
 */
export function loadMissionRecord(record: MissionRecord): void {
  const { projectId: _projectId, ownerId: _ownerId, createdAt: _createdAt, updatedAt: _updatedAt, version, ...document } = record;
  const current = useMissionStore.getState();
  if (current.id !== record.id || current.version !== version) {
    useTelemetryStore.getState().reset();
    loadRun(0);
  }
  useMissionStore.getState().load(record.id, document, version);
}

/** Clear the open mission and any telemetry that belonged to it. */
export function clearMission(): void {
  useMissionStore.getState().clear();
  useTelemetryStore.getState().reset();
  loadRun(0);
}

export function addManeuver(maneuver: ManeuverDto): void {
  const { document, patch } = useMissionStore.getState();
  if (!document) return;
  patch({ maneuvers: [...document.maneuvers, maneuver] });
}

export function updateManeuver(maneuverId: string, update: Partial<ManeuverDto>): void {
  const { document, patch } = useMissionStore.getState();
  if (!document) return;
  patch({
    maneuvers: document.maneuvers.map((maneuver) => (maneuver.id === maneuverId ? { ...maneuver, ...update } : maneuver)),
  });
}

export function removeManeuver(maneuverId: string): void {
  const { document, patch } = useMissionStore.getState();
  if (!document) return;
  patch({ maneuvers: document.maneuvers.filter((maneuver) => maneuver.id !== maneuverId) });
}
